// API Service for making HTTP requests
class ApiService {
  constructor() {
    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      this.baseURL = 'http://localhost/clicklooks/api/api';
    } else if (hostname === "clicklooks.com") {
      this.baseURL = 'https://clicklooks.com/api/api';
    } else if (hostname === "staging.clicklooks.com") {
      this.baseURL = 'https://staging.clicklooks.com/api/api';
    }
    this.instance = null;
  }

  static getInstance() {
    if (!this.instance) {
      this.instance = new ApiService();
    }
    return this.instance;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    const isFormData = options.body instanceof FormData;

    const config = {
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...options.headers,
      },
      credentials: 'include',
      ...options,
    };

    // If using FormData, ensure we do NOT set Content-Type manually (browser sets boundary).
    if (isFormData && config.headers && config.headers['Content-Type']) {
      delete config.headers['Content-Type'];
    }

    // Add session token if available
    const sessionToken = sessionStorage.getItem('session_token') || localStorage.getItem('session_token');
    console.log('🔑 ApiService - Checking sessionStorage for token...');
    console.log('🔑 ApiService - Token found:', sessionToken ? 'YES (' + sessionToken.substring(0, 20) + '...)' : 'NO');
    console.log('🔑 ApiService - All sessionStorage keys:', Object.keys(sessionStorage));
    
    if (sessionToken) {
      config.headers['Authorization'] = `Bearer ${sessionToken}`;
      console.log('✅ ApiService - Authorization header SET with token');
    } else {
      console.error('❌ ApiService - NO TOKEN in sessionStorage! Cannot add Authorization header.');
      console.error('❌ ApiService - This will cause 401 errors. Please login again.');
    }



    try {
      console.log('ApiService - Making request to:', url);
      console.log('ApiService - Request headers:', { ...config.headers, Authorization: config.headers['Authorization'] ? config.headers['Authorization'].substring(0, 20) + '...' : 'NOT SET' });
      const response = await fetch(url, config);
      console.log('ApiService - Response status:', response.status, response.statusText);
      const data = await response.json();
      console.log('ApiService - Response data:', data);
      

      if (!response.ok) {
        // Handle authentication errors: if token is invalid/expired, logout + redirect to login.
        // Backend typically responds with 401 and message like "Invalid or expired token".
        if (response.status === 401) {
          const sessionToken =
            sessionStorage.getItem('session_token') || localStorage.getItem('session_token');

          // Keep parity with the TS api service: allow bypass sessions to handle 401 without redirecting.
          const isAdminBypass =
            sessionToken && String(sessionToken).startsWith('admin_bypass_token_');

          if (!isAdminBypass) {
            // Clear session data
            try {
              sessionStorage.clear();
            } catch (e) {
              // ignore
            }
            localStorage.removeItem('session_token');
            localStorage.removeItem('user');
            localStorage.removeItem('tenant');
            localStorage.removeItem('userFeatures');

            // Redirect to login (avoid loops if already there)
            if (window.location.pathname !== '/login') {
              // `replace` avoids polluting history and is typically a bit more immediate.
              window.location.replace('/login');
            }

            // IMPORTANT:
            // Do NOT return a never-resolving promise here. That pattern causes pages guarded
            // by permission/feature checks to hang forever (blank screens / stuck spinners).
            // Instead, throw an auth error so callers can unwind loading state; the redirect
            // above will take the user to /login.
            const authError = new Error(data?.message || 'Unauthorized');
            authError.isAuthError = true;
            authError.redirectedToLogin = true;
            authError.response = response;
            authError.responseData = data;
            throw authError;
          }

          // Admin bypass: let caller handle the error.
          const authError = new Error(data?.message || 'Unauthorized');
          authError.isAuthError = true;
          authError.response = response;
          authError.responseData = data;
          throw authError;
        }

        // Handle permission denied errors
        console.log('ApiService - permission denied',response);
        if (response.status === 403) {
          // Check if subscription expired
          if (data.subscription_expired) {
            // Subscription expired - show error message
            const error = new Error(data.message || 'Subscription expired. Please contact support.');
            error.subscription_expired = true;
            throw error;
          }
          // Check if this is an email verification error
          if (data.error && data.error.includes('email')) {
            const error = new Error(data.error);
            error.response = response;
            throw error;
          }
          const error = new Error('Permission denied');
          error.response = response;
          throw error;
        }

        const error = new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
        error.response = response;
        error.responseData = data;
        throw error;
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  async get(endpoint, params = {}) {
    const searchParams = new URLSearchParams(params);
    const url = searchParams.toString() ? `${endpoint}?${searchParams}` : endpoint;

    return this.request(url, {
      method: 'GET',
    });
  }

  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async postFormData(endpoint, formData) {
    return this.request(endpoint, {
      method: 'POST',
      body: formData,
    });
  }

  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE',
    });
  }
}

// Create and export a singleton instance
const apiService = ApiService.getInstance();
export default apiService;
